import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageSEO } from '../components/SEO/PageSEO';
import Mixpanel from '../utils/mixpanel';

// FAQ Schema for Inspo page
const inspoPageFaqSchema = [
  {
    question: "Can I book a venue I see in the LetsHost inspiration gallery?",
    answer: "Yes! Our gallery features real venues from our NYC network. Submit an event inquiry through our booking form, and our concierge will match you with venues that fit your style and requirements."
  },
  {
    question: "What types of events are featured in the gallery?",
    answer: "Our gallery showcases corporate dinners, private dining rooms, cocktail parties, engagement celebrations, rehearsal dinners, and full restaurant buyouts. Each photo is tagged by event type and venue style."
  },
  {
    question: "Do the gallery photos show actual events or staged shoots?",
    answer: "Most photos in our gallery are from real events hosted at these venues. This gives you a realistic view of how spaces look when set up for celebrations, dinners, and corporate gatherings."
  },
  {
    question: "How do I find venues for a specific type of event?",
    answer: "Use the filter buttons to browse by category: Dining, Rooftop, Happy Hour, Cocktail Party, Corporate, Birthday, or Engagement. Each venue is tagged with event types it's best suited for."
  }
];

export default function Inspo() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [hoveredItem, setHoveredItem] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});

  const galleryItems = useMemo(() => [
    {
      src: "/Inspo_Gallery/long_table_red_dark_romantic_room.jpeg",
      alt: "Long table in a red, dark, romantic dining room",
      category: "Dining",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/many small tables.avif",
      alt: "Dining room with many small tables arranged for a group event",
      category: "Dining",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/super_high_end_private_dining_for_ten.jpg",
      alt: "Super high-end private dining setup for ten guests",
      category: "Dining",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/Dirty-French-Private-Dining-scaled.jpeg",
      alt: "Dirty French private dining room, upscale and stylish",
      category: "Dining",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/high_end_private_dining_for 25.webp",
      alt: "High-end private dining setup for 25 guests",
      category: "Dining",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/private_dining_nice_stylish.webp",
      alt: "Private dining room, nice and stylish decor",
      category: "Dining",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/private_dining_for_20_dim_wine_cellar.webp",
      alt: "Private dining for 20 in a dimly lit wine cellar",
      category: "Dining",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-champagne-tower.jpg",
      alt: "Elegant champagne tower at an NYC engagement party",
      category: "Engagement",
      tags: ["Celebration", "Cocktail Party"]
    },
    {
      src: "/Inspo_Gallery/private-dinner-cellar.jpeg",
      alt: "Elegant Manhattan restaurant private dining in a cellar setting",
      category: "Dining",
      tags: ["Celebration", "Corporate"]
    },
    {
      src: "/Inspo_Gallery/ArloSoho-scaled-1.jpg",
      alt: "Arlo SoHo rooftop venue with stunning NYC views",
      category: "Rooftop",
      tags: ["Celebration", "Cocktail Party"]
    },
    {
      src: "/Inspo_Gallery/copinette-nice-corporate-happy-hour.png",
      alt: "Corporate happy hour at Copinette restaurant",
      category: "Happy Hour",
      tags: ["Corporate", "Cocktail Party"]
    },
    {
      src: "/Inspo_Gallery/cocktail-party.jpg",
      alt: "Lively cocktail party in New York",
      category: "Cocktail Party",
      tags: ["Celebration", "Birthday"]
    },
    {
      src: "/Inspo_Gallery/baar-baar-cocktail-bar-event-space.jpg",
      alt: "Stylish cocktail bar with modern design",
      category: "Cocktail Party",
      tags: ["Celebration", "Birthday", "Corporate"]
    },
    {
      src: "/Inspo_Gallery/bandits-fun-birthday-party.jpg",
      alt: "Fun birthday celebration at Bandits",
      category: "Birthday",
      tags: ["Celebration", "Cocktail Party"]
    },
    {
      src: "/Inspo_Gallery/prive-engagement-party-cocktails-party.png",
      alt: "Elegant engagement party with cocktails at Prive",
      category: "Engagement",
      tags: ["Celebration", "Cocktail Party"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-group-shot.png",
      alt: "Group photo at an engagement celebration",
      category: "Engagement",
      tags: ["Celebration", "Birthday"]
    },
    {
      src: "/Inspo_Gallery/moabet-shabbat-club-social-party.jpg",
      alt: "Social gathering at Shabbat Club",
      category: "Cocktail Party",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/dinner-table-setup-moabet.jpg",
      alt: "Elegant dinner table setup for special occasions",
      category: "Dining",
      tags: ["Celebration", "Corporate", "Engagement"]
    },
    {
      src: "/Inspo_Gallery/hidden-lane-outdoor-happy-hour.png",
      alt: "Outdoor happy hour at Hidden Lane",
      category: "Happy Hour",
      tags: ["Cocktail Party", "Corporate"]
    },
    {
      src: "/Inspo_Gallery/copinette-dining-main-room.png",
      alt: "Elegant main dining room at Copinette",
      category: "Dining",
      tags: ["Corporate", "Celebration", "Engagement"]
    },
    {
      src: "/Inspo_Gallery/bar-bonobo-nice-entry-way-cocktail-bar.png",
      alt: "Stylish entrance at Bar Bonobo cocktail lounge",
      category: "Cocktail Party",
      tags: ["Happy Hour", "Corporate"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-large-bar.png",
      alt: "Large bar setup for engagement celebration",
      category: "Engagement",
      tags: ["Celebration", "Cocktail Party"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-games1.jpg",
      alt: "Fun games at an engagement celebration",
      category: "Engagement",
      tags: ["Celebration", "Birthday"]
    },
    {
      src: "/Inspo_Gallery/nice-outdoor-engagement-party.jpg",
      alt: "Beautiful outdoor engagement gathering",
      category: "Engagement",
      tags: ["Celebration", "Rooftop"]
    },
    {
      src: "/Inspo_Gallery/bandits-cute-couch-booth-engagement-photo.jpg",
      alt: "Cozy booth seating for intimate gatherings",
      category: "Engagement",
      tags: ["Celebration", "Birthday", "Dining"]
    },
    {
      src: "/Inspo_Gallery/Beautini+Prive+Dinner+Kylee+Yee-118_websize.jpg",
      alt: "Elegant private dinner setup",
      category: "Dining",
      tags: ["Corporate", "Celebration", "Engagement"]
    },
    {
      src: "/Inspo_Gallery/cocktail bar.jpg",
      alt: "Stylish cocktail bar setting",
      category: "Cocktail Party",
      tags: ["Happy Hour", "Birthday", "Celebration"]
    },
    {
      src: "/Inspo_Gallery/social-party-shabbat-club.jpeg",
      alt: "Social gathering at Shabbat Club",
      category: "Cocktail Party",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/moabet-private-dining-setup.jpg",
      alt: "Private dining setup at Moabet",
      category: "Dining",
      tags: ["Corporate", "Celebration", "Engagement"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-rooftop-fancy.jpeg",
      alt: "Fancy rooftop engagement party",
      category: "Engagement",
      tags: ["Rooftop", "Celebration"]
    },
    {
      src: "/Inspo_Gallery/private-dinner-buddakan.jpeg",
      alt: "Private dinner at Buddakan",
      category: "Dining",
      tags: ["Corporate", "Celebration"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-dancing.png",
      alt: "Dancing at an engagement celebration",
      category: "Engagement",
      tags: ["Celebration", "Birthday"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-group-outdoor-shot.png",
      alt: "Outdoor group photo at an engagement party",
      category: "Engagement",
      tags: ["Celebration", "Rooftop"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-fancy-older-crowd.png",
      alt: "Fancy engagement party with elegant crowd",
      category: "Engagement",
      tags: ["Celebration", "Corporate"]
    },
    {
      src: "/Inspo_Gallery/bar-louis-leather-couches.png",
      alt: "Stylish bar with leather couches",
      category: "Cocktail Party",
      tags: ["Happy Hour", "Birthday"]
    },
    {
      src: "/Inspo_Gallery/copinette-private-dinner.png",
      alt: "Private dinner setup at Copinette",
      category: "Dining",
      tags: ["Corporate", "Celebration"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-ring-look.jpg",
      alt: "Engagement ring celebration moment",
      category: "Engagement",
      tags: ["Celebration"]
    },
    {
      src: "/Inspo_Gallery/nice-private-dinner-setup-moabet.jpg",
      alt: "Elegant private dinner setup at Moabet",
      category: "Dining",
      tags: ["Corporate", "Celebration"]
    },
    {
      src: "/Inspo_Gallery/food-spread-on-table-bridal-shower.jpg",
      alt: "Food spread for a celebration",
      category: "Dining",
      tags: ["Celebration", "Birthday"]
    },
    {
      src: "/Inspo_Gallery/engagement-party-rooftop-red-light.jpg",
      alt: "Rooftop engagement party with red lighting",
      category: "Engagement",
      tags: ["Rooftop", "Celebration"]
    },
    {
      src: "/Inspo_Gallery/prive-insta-4-19-25.jpg",
      alt: "Stylish gathering at Prive",
      category: "Celebration",
      tags: ["Birthday", "Cocktail Party"]
    },
  ], []);

  const preferredOrder = ['All', 'Celebration', 'Dining', 'Rooftop', 'Happy Hour', 'Cocktail Party', 'Corporate', 'Birthday', 'Engagement'];

  const uniqueCategories = new Set(galleryItems.map(item => item.category));
  const categories = preferredOrder.filter(category =>
    category === 'All' || uniqueCategories.has(category)
  );

  const handleImageLoad = (index) => {
    setLoadedImages(prev => ({ ...prev, [index]: true }));
  };

  const handleImageError = (e, index) => {
    e.target.onerror = null;
    e.target.src = "/Inspo_Gallery/cocktail-party.jpg";
    setLoadedImages(prev => ({ ...prev, [index]: true }));
  };

  const handleFilterChange = (category) => {
    setActiveFilter(category);
    Mixpanel.track('Inspo Filter Change', { category });
  };

  React.useEffect(() => {
    Mixpanel.track('Inspo Page View', {
      timestamp: new Date().toISOString(),
    });
  }, []);

  const filteredItems = activeFilter === 'All'
    ? galleryItems.filter((item, index, self) =>
        index === self.findIndex(t => t.src === item.src))
    : galleryItems.filter(item =>
        (item.category === activeFilter ||
        (item.tags && item.tags.includes(activeFilter)))).filter((item, index, self) =>
        index === self.findIndex(t => t.src === item.src));

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageSEO
        title="NYC Event Venue Inspiration Gallery | LetsHost NYC"
        description="Browse our gallery of NYC private dining rooms, corporate event spaces, cocktail party venues, and engagement celebration locations. Real event photos from Manhattan's best venues."
        path="/inspo"
        ogImage="/letshost-share-preview.png"
        faqSchema={inspoPageFaqSchema}
      />

      <Navbar />

      {/* ───────── GALLERY ───────── */}
      <section id="gallery" className="pt-6 pb-16 sm:pt-8 sm:pb-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Browse by Style
            </h2>
            <p className="text-base sm:text-lg text-stone-500 max-w-lg mx-auto">
              Filter by event type to find inspiration for your celebration
            </p>
          </motion.div>

          {/* Filter buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex flex-wrap justify-center gap-2 mb-10"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleFilterChange(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeFilter === category
                    ? 'bg-stone-900 text-white shadow-md'
                    : 'bg-[#F5F0EB] text-stone-600 hover:bg-stone-200 border border-stone-200'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {/* Gallery grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            <AnimatePresence mode="wait">
              {filteredItems.map((item, index) => (
                <motion.div
                  layout
                  key={item.src}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: index * 0.02 }}
                  className="relative group overflow-hidden rounded-xl bg-stone-100 aspect-square"
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-full object-cover transition-transform duration-500 ease-out"
                    style={{
                      transform: hoveredItem === index ? 'scale(1.05)' : 'scale(1)',
                      opacity: loadedImages[index] ? 1 : 0,
                    }}
                    onLoad={() => handleImageLoad(index)}
                    onError={(e) => handleImageError(e, index)}
                    loading={index < 8 ? "eager" : "lazy"}
                  />

                  {/* Info overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent transition-opacity duration-300 flex flex-col justify-end p-4 ${
                      hoveredItem === index ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-white/90 text-stone-800 mb-2 w-fit">
                      {item.category}
                    </span>
                    {item.tags && item.tags.slice(0, 2).map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="inline-block px-2 py-0.5 rounded-full text-xs text-white/90 bg-stone-600/70 mr-2 mb-1"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Empty state */}
          {filteredItems.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <p className="text-stone-500 text-lg mb-4">No photos found in this category</p>
              <button
                onClick={() => setActiveFilter('All')}
                className="px-6 py-2 bg-stone-900 text-white rounded-lg text-sm font-medium hover:bg-stone-700 transition-colors"
              >
                View All
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ready to Find Your Venue?
            </h2>
            <p className="text-base sm:text-lg text-stone-500 mb-9 max-w-lg mx-auto">
              Let us match you with the perfect NYC venue for your event vision.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg font-medium text-white bg-stone-900 hover:bg-stone-700 transition-colors text-base shadow-md"
              >
                Get Started
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
              <a
                href="mailto:events@letshost.nyc"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg font-medium text-stone-700 bg-white border border-stone-300 hover:bg-stone-50 transition-colors text-base"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
